import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState<string | null>(null);

  const toggleMenu = (menuName: string) => {
    setOpenMenu(openMenu === menuName ? null : menuName);
  };

  const isActive = (path: string) => location.pathname === path;

  // 🔒 Fermer automatiquement le sidebar après navigation (mobile)
  useEffect(() => {
    onClose();
  }, [location.pathname]);

  const menuItems = [
    { name: 'dashboard', label: 'Tableau de Bord', icon: 'ri-dashboard-line', path: '/dashboard' },
    {
      name: 'sales',
      label: 'Ventes',
      icon: 'ri-shopping-cart-line',
      submenu: [
        { name: 'clients', label: 'Clients', icon: 'ri-user-line', path: '/customers' },
        { label: 'Devis', path: '/quotes', icon: 'ri-file-text-line' },
        { label: 'Commandes Clients', path: '/sales', icon: 'ri-file-list-line' },
        { label: 'Factures Clients', path: '/invoices', icon: 'ri-bill-line' }
      ]
    },
    {
      name: 'purchases',
      label: 'Achats',
      icon: 'ri-shopping-bag-line',
      submenu: [
        { label: 'Bons de Commande', path: '/purchase-orders', icon: 'ri-file-list-3-line' },
        { label: 'Fournisseurs', path: '/suppliers', icon: 'ri-truck-line' }
      ]
    },
    { name: 'inventory', label: 'Stock', icon: 'ri-stack-line', path: '/products' },
    { name: 'cash-register', label: 'Caisse', icon: 'ri-cash-line', path: '/cash-register' },
    { name: 'finance', label: 'Finance', icon: 'ri-money-dollar-circle-line', path: '/finance' },
    { name: 'hr', label: 'Ressources Humaines', icon: 'ri-team-line', path: '/employees' },
    { name: 'reports', label: 'Rapports', icon: 'ri-bar-chart-box-line', path: '/reports' },
    { name: 'admin', label: 'Administration', icon: 'ri-settings-3-line', path: '/admin' },
  ];

  return (
    <>
      {/* Overlay mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 z-50 transform transition-transform duration-300
        ${isOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}
      >
        {/* Header */}
        <div className="h-20 p-6 border-b border-gray-200 flex items-center">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <i className="ri-building-2-fill text-white text-xl"></i>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900">MAI MAB</h1>
              <p className="text-xs text-gray-500">Services</p>
            </div>
          </div>
        </div>

        {/* MENU (scrollable) */}
        <nav className="flex-1 overflow-y-auto p-4">
          <ul className="space-y-1">
            {menuItems.map((item) => (
              <li key={item.name}>
                {item.submenu ? (
                  <>
                    <button
                      onClick={() => toggleMenu(item.name)}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg ${
                        openMenu === item.name
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <i className={`${item.icon} text-lg`}></i>
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <i className={`ri-arrow-down-s-line transition-transform ${openMenu === item.name ? 'rotate-180' : ''}`} />
                    </button>

                    {openMenu === item.name && (
                      <ul className="mt-1 ml-4 space-y-1">
                        {item.submenu.map((sub) => (
                          <li key={sub.path}>
                            <Link
                              to={sub.path}
                              className={`flex items-center space-x-3 px-4 py-2 rounded-lg ${
                                isActive(sub.path)
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-600 hover:bg-gray-50'
                              }`}
                            >
                              <i className={`${sub.icon} text-sm`} />
                              <span className="text-sm">{sub.label}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                ) : (
                  <Link
                    to={item.path!}
                    className={`flex items-center space-x-3 px-4 py-3 rounded-lg ${
                      isActive(item.path!)
                        ? 'bg-blue-600 text-white'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <i className={`${item.icon} text-lg`} />
                    <span className="font-medium">{item.label}</span>
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </nav>

        {/* Footer (fixe) */}
        <div className="h-20 p-4 border-t border-gray-200 bg-white">
          <div className="flex items-center space-x-3 px-4 py-2 bg-gray-50 rounded-lg">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <span className="text-white font-semibold">AD</span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">Administrateur</p>
              <p className="text-xs text-gray-500">admin@maimab.com</p>
            </div>
            <Link to="/login" className="text-gray-400 hover:text-red-600">
              <i className="ri-logout-box-line text-lg"></i>
            </Link>
          </div>
        </div>
      </aside>
    </>
  );
}
